/**
 * Homepage Critical JavaScript
 * Loads immediately for essential homepage functionality
 * Heavy features (animations, carousels) are lazy-loaded
 */

import { lazyLoader } from './utils/lazy-loader.js';

(function() {
  'use strict';

  // Homepage rotator is already loaded via separate script tag (home-rotator.js)
  // We just need to set up lazy loading for non-critical features

  function lazyLoadModule(selector, importFn, options = {}) {
    const { triggerEvent = null, delay = 0 } = options;

    if (selector) {
      const element = document.querySelector(selector);
      if (element) {
        if (triggerEvent === 'visible') {
          // Load when element becomes visible
          const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
              setTimeout(() => {
                importFn().then(module => module.init && module.init());
              }, delay);
              observer.disconnect();
            }
          }, { rootMargin: '50px' });
          observer.observe(element);
        } else if (triggerEvent === 'click') {
          // Load on first click
          element.addEventListener('click', function handler(e) {
            e.preventDefault();
            importFn().then(module => {
              module.init && module.init();
              element.click(); // Re-trigger the click
            });
            element.removeEventListener('click', handler);
          }, { once: true });
        } else {
          // Load immediately
          setTimeout(() => {
            importFn().then(module => module.init && module.init());
          }, delay);
        }
      }
    } else {
      // No selector, load after delay
      setTimeout(() => {
        importFn().then(module => module.init && module.init());
      }, delay);
    }
  }

  document.addEventListener('DOMContentLoaded', function() {
    // Lazy-load animations after page load (1 second delay)
    window.addEventListener('load', () => {
      lazyLoadModule(null, () => import('./modules/animations-module.js'), { delay: 1000 });
    });

    // Lazy-load Flickity carousels when they become visible
    if (document.querySelector('[data-flickity]')) {
      lazyLoadModule('[data-flickity]', () => import('./modules/flickity-module.js').then(m => ({ init: m.initFlickity })), { triggerEvent: 'visible' });
    }

    // Set up lazy Fancybox (if any gallery images exist)
    if (document.querySelector('[data-fancybox]')) {
      import('./modules/fancybox-module.js').then(module => {
        module.setupLazyFancybox();
      });
    }
  });
})();
